import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Option entry for {@link FloatingSelect}. */
export interface FloatingSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Floating-label single-select control mirroring Purolator UI kit visuals.
 *
 * Short-term exception: replaces `puro-select` because the kit combobox fails in
 * nested reactive-form layouts (option clicks close the list without updating the
 * value). Track removal once upstream is fixed — dsc-online-shipping-ui#floating-select-kit-workaround.
 */
@Component({
  selector: 'app-floating-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.floating-select-host--full-width]': 'fullWidth()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FloatingSelect),
      multi: true,
    },
  ],
  templateUrl: './floating-select.html',
  styleUrl: './floating-select.scss',
})
export class FloatingSelect implements ControlValueAccessor, OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly label = input('');
  readonly options = input<FloatingSelectOption[]>([]);
  readonly required = input(false);
  readonly fullWidth = input(false);
  readonly error = input(false);
  readonly errorMessage = input('');

  protected selectedValue: string | null = null;
  protected isOpen = false;
  protected disabled = false;
  protected activeOptionIndex = -1;

  private static nextId = 0;
  protected readonly selectId = `floating-select-${FloatingSelect.nextId++}`;

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private typeaheadBuffer = '';
  private typeaheadTimeoutId: number | undefined;

  private readonly clickOutsideHandler = (event: Event): void => {
    const path =
      typeof event.composedPath === 'function'
        ? event.composedPath()
        : [event.target as EventTarget];
    window.setTimeout(() => {
      if (path.includes(this.elementRef.nativeElement)) {
        return;
      }
      if (this.isOpen) {
        this.closeList();
      }
      this.blurField();
    }, 0);
  };

  ngOnInit(): void {
    document.addEventListener('mousedown', this.clickOutsideHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.clickOutsideHandler);
    this.clearTypeaheadTimeout();
  }

  writeValue(value: string | null): void {
    this.selectedValue = value === '' ? null : value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  protected get hasValue(): boolean {
    return this.selectedValue !== null && this.selectedValue !== '';
  }

  protected get displayText(): string {
    if (!this.hasValue) {
      return '';
    }
    return this.options().find((option) => option.value === this.selectedValue)?.label ?? '';
  }

  protected get enabledOptions(): FloatingSelectOption[] {
    return this.options().filter((option) => !option.disabled);
  }

  protected get activeDescendantId(): string | null {
    if (!this.isOpen || this.activeOptionIndex < 0) {
      return null;
    }
    const option = this.enabledOptions[this.activeOptionIndex];
    return option ? this.optionId(option.value) : null;
  }

  protected get containerClasses(): string {
    return [
      'floating-select',
      this.fullWidth() ? 'floating-select--full-width' : '',
      this.error() ? 'floating-select--error' : '',
      this.isOpen ? 'floating-select--open' : '',
      this.disabled ? 'floating-select--disabled' : '',
      this.hasValue ? 'floating-select--has-value' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  protected optionId(value: string): string {
    return `${this.selectId}-option-${value}`;
  }

  protected isHighlighted(value: string): boolean {
    if (!this.isOpen || this.activeOptionIndex < 0) {
      return false;
    }
    return this.enabledOptions[this.activeOptionIndex]?.value === value;
  }

  protected toggleList(): void {
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.closeList();
      return;
    }
    this.openList();
  }

  protected onFieldKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    const options = this.enabledOptions;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.handleArrowKey('down', options);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.handleArrowKey('up', options);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen) {
          this.openList();
        } else if (this.activeOptionIndex >= 0) {
          this.commitOption(options[this.activeOptionIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeList();
        this.blurField();
        break;
      case 'Home':
        this.handleEdgeKey('home', options, event);
        break;
      case 'End':
        this.handleEdgeKey('end', options, event);
        break;
      case 'Tab':
        if (this.isOpen) {
          this.closeList();
        }
        break;
      default:
        if (this.isTypeaheadKey(event)) {
          event.preventDefault();
          this.handleTypeahead(event.key);
        }
    }
  }

  /** Moves active option up or down; opens list if it is closed. */
  private handleArrowKey(direction: 'down' | 'up', options: FloatingSelectOption[]): void {
    if (!this.isOpen) {
      this.openList();
      return;
    }
    this.activeOptionIndex =
      direction === 'down'
        ? Math.min(this.activeOptionIndex + 1, options.length - 1)
        : Math.max(this.activeOptionIndex - 1, 0);
    this.cdr.markForCheck();
  }

  /** Jumps active option to the first or last position when the list is open. */
  private handleEdgeKey(
    edge: 'home' | 'end',
    options: FloatingSelectOption[],
    event: KeyboardEvent,
  ): void {
    if (!this.isOpen) {
      return;
    }
    event.preventDefault();
    this.activeOptionIndex = edge === 'home' ? 0 : Math.max(options.length - 1, 0);
    this.cdr.markForCheck();
  }

  protected onFieldBlur(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget;
    window.setTimeout(() => {
      if (relatedTarget && this.elementRef.nativeElement.contains(relatedTarget as Node)) {
        return;
      }
      if (this.isOpen) {
        this.closeList();
      }
    }, 0);
  }

  protected selectOption(option: FloatingSelectOption, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.commitOption(option);
  }

  protected isSelected(value: string): boolean {
    return this.selectedValue === value;
  }

  private openList(): void {
    const options = this.enabledOptions;
    const selectedIndex = options.findIndex((option) => option.value === this.selectedValue);
    this.isOpen = true;
    this.activeOptionIndex = Math.max(selectedIndex, 0);
    this.cdr.markForCheck();
  }

  private commitOption(option: FloatingSelectOption | undefined): void {
    if (!option || option.disabled) {
      return;
    }
    this.selectedValue = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.closeList();
    this.blurField();
  }

  private closeList(): void {
    this.isOpen = false;
    this.activeOptionIndex = -1;
    this.clearTypeaheadBuffer();
    this.onTouched();
    this.cdr.markForCheck();
  }

  private blurField(): void {
    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      this.elementRef.nativeElement.contains(activeElement)
    ) {
      activeElement.blur();
    }
    this.cdr.markForCheck();
  }

  private isTypeaheadKey(event: KeyboardEvent): boolean {
    return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
  }

  private handleTypeahead(char: string): void {
    if (!this.isOpen) {
      this.openList();
    }

    this.typeaheadBuffer += char.toLowerCase();
    this.clearTypeaheadTimeout();
    this.typeaheadTimeoutId = window.setTimeout(() => this.clearTypeaheadBuffer(), 500);

    const matchIndex = this.enabledOptions.findIndex((option) =>
      option.label.toLowerCase().startsWith(this.typeaheadBuffer),
    );

    if (matchIndex >= 0) {
      this.activeOptionIndex = matchIndex;
      this.cdr.markForCheck();
    }
  }

  private clearTypeaheadBuffer(): void {
    this.typeaheadBuffer = '';
    this.clearTypeaheadTimeout();
  }

  private clearTypeaheadTimeout(): void {
    if (this.typeaheadTimeoutId !== undefined) {
      window.clearTimeout(this.typeaheadTimeoutId);
      this.typeaheadTimeoutId = undefined;
    }
  }
}
